import { useEffect } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { services } from "@/data/services";
import { useSectionNav } from "@/lib/scroll";

export default function ServicePage() {
  const { slug } = useParams();
  const service = services.find((s) => s.slug === slug);
  const sectionNav = useSectionNav();

  useEffect(() => {
    if (service) {
      document.title = `${service.title} in Mumbai | Saurabh Decor Creations`;
    }
  }, [service]);

  if (!service) return <Navigate to="/" replace />;
  const others = services.filter((s) => s.slug !== slug);

  return (
    <main data-testid={`service-page-${slug}`}>
      <section className="relative h-[55vh] min-h-[420px] flex items-end">
        <img src={service.image} alt={service.alt} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pb-16 w-full">
          <p className="text-xs tracking-[0.25em] uppercase font-semibold text-zinc-300 mb-4">Our Services</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-white">
            {service.title}
          </h1>
          <p className="mt-4 text-base lg:text-lg text-zinc-200 max-w-2xl">{service.tagline}</p>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16">
          <div>
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-zinc-500 mb-5">Overview</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-zinc-950 leading-tight">
              Professional {service.title} in Mumbai
            </h2>
            {service.intro.map((p, i) => (
              <p key={i} className="mt-6 text-base leading-relaxed text-zinc-600">{p}</p>
            ))}
            <button
              data-testid="service-page-quote-btn"
              onClick={() => sectionNav("contact")}
              className="mt-10 inline-flex items-center gap-2 bg-zinc-950 text-white text-sm font-semibold px-8 py-4 rounded-sm hover:bg-zinc-800 transition-colors duration-300"
            >
              Get a Free Quote <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
          <div className="border border-zinc-200 rounded-sm p-8 lg:p-10 h-fit">
            <h3 className="font-heading text-xl font-medium text-zinc-950">What&apos;s Included</h3>
            <ul className="mt-6 space-y-4">
              {service.includes.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-zinc-600">
                  <Check className="w-4 h-4 text-zinc-950 shrink-0 mt-0.5" strokeWidth={1.5} />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-20 bg-zinc-50 border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <h2 className="font-heading text-2xl sm:text-3xl font-light tracking-tight text-zinc-950 mb-10">
            Explore Other Services
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {others.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`service-other-${s.slug}`}
                className="group bg-white border border-zinc-200 rounded-sm p-6 hover:border-zinc-950 transition-colors duration-300"
              >
                <h3 className="font-heading text-lg font-medium text-zinc-950">{s.title}</h3>
                <span className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-zinc-500 group-hover:text-zinc-950 group-hover:gap-3 transition-all duration-300">
                  View Service <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
